import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeClosed, Check, } from "lucide-react";

const SignupForm = () => {
  const { dispatch } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // const [role, setRole] = useState("Company"); // Default role
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordChecklist, setShowPasswordChecklist] = useState(false);
  const navigate = useNavigate();

  // Password validation criteria
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  // Validate password against criteria
  useEffect(() => {
    setPasswordCriteria({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    });
  }, [password]);

  // Check if all password criteria are met
  const isPasswordValid = Object.values(passwordCriteria).every(Boolean);

  const handleSignup = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password meets all criteria
    if (!isPasswordValid) {
      setError("Password does not meet all requirements");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log("Sending signup request...");
      const response = await apiService.post("/api/auth/register", null, {
        email,
        password,
        role: "Company",
      });

      console.log("Signup response:", response.data);
      if (response.data?.token) {
        dispatch({
          type: "LOGIN",
          payload: { token: response.data.accessToken, user: response.data.user },
        });
        console.log("User registered, navigating to dashboard...");
        navigate("/dashboard");
      } else {
        console.log("Registration successful, navigating to login...");
        navigate("/login");
      }
    } catch (err) {
      console.error("Signup error:", err.response?.data || err.message);
      setError(
        err.response?.data?.message || err.message || "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl text-center font-bold text-gray-800 mb-6">
        Create your account
      </h1>

      <form onSubmit={handleSignup} className="space-y-5">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email address
          </label>
          <div className="relative group">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:ring-1 focus:ring-primary-600 focus:border-transparent transition-all duration-200 outline-none"
              required
            />
            <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-200 group-focus-within:w-full"></div>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="relative group">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setShowPasswordChecklist(true)}
              // Optionally, uncomment below to hide the checklist when the input loses focus
              // onBlur={() => setShowPasswordChecklist(false)}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:ring-1 focus:ring-primary-600 focus:border-transparent transition-all duration-200 outline-none"
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <Eye size={20} /> : <EyeClosed size={20} />}
            </button>
            <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-200 group-focus-within:w-full"></div>
          </div>

          {/* Password Checklist */}
          {showPasswordChecklist && (
            <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Password must have:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm">
                  {passwordCriteria.length ? (
                    <Check size={16} className="mr-2 text-primary-600" />
                  ) : (
                    <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                      <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                    </div>
                  )}
                  <span
                    className={
                      passwordCriteria.length ? "text-primary-600" : "text-gray-600"
                    }
                  >
                    At least 8 characters
                  </span>
                </li>
                <li className="flex items-center text-sm">
                  {passwordCriteria.uppercase ? (
                    <Check size={16} className="mr-2 text-primary-600" />
                  ) : (
                    <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                      <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                    </div>
                  )}
                  <span
                    className={
                      passwordCriteria.uppercase
                        ? "text-primary-600"
                        : "text-gray-600"
                    }
                  >
                    At least one uppercase letter
                  </span>
                </li>
                <li className="flex items-center text-sm">
                  {passwordCriteria.lowercase ? (
                    <Check size={16} className="mr-2 text-primary-600" />
                  ) : (
                    <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                      <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                    </div>
                  )}
                  <span
                    className={
                      passwordCriteria.lowercase
                        ? "text-primary-600"
                        : "text-gray-600"
                    }
                  >
                    At least one lowercase letter
                  </span>
                </li>
                <li className="flex items-center text-sm">
                  {passwordCriteria.number ? (
                    <Check size={16} className="mr-2 text-primary-600" />
                  ) : (
                    <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                      <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                    </div>
                  )}
                  <span
                    className={
                      passwordCriteria.number
                        ? "text-primary-600"
                        : "text-gray-600"
                    }
                  >
                    At least one number
                  </span>
                </li>
                <li className="flex items-center text-sm">
                  {passwordCriteria.special ? (
                    <Check size={16} className="mr-2 text-primary-600" />
                  ) : (
                    <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                      <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                    </div>
                  )}
                  <span
                    className={
                      passwordCriteria.special
                        ? "text-primary-600"
                        : "text-gray-600"
                    }
                  >
                    At least one special character
                  </span>
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="confirmPassword"
            className="text-sm font-medium text-gray-700"
          >
            Confirm Password
          </label>
          <div className="relative group">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:ring-1 focus:ring-primary-600 focus:border-transparent transition-all duration-200 outline-none"
              required
            />
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <Eye size={20} /> : <EyeClosed size={20} />}
            </button>
            <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-200 group-focus-within:w-full"></div>
          </div>
        </div>

        {/* <div className="space-y-2">
          <label htmlFor="role" className="text-sm font-medium text-gray-700">
            Role
          </label>
          <div className="relative group">
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:ring-1 focus:ring-primary-600 focus:border-transparent transition-all duration-200 outline-none"
              required
            >
              <option value="Company">Company</option>
              Uncomment and add more roles as needed:
              <option value="Manager">Manager</option>
              <option value="HR">HR</option>
              <option value="Administrator">Administrator</option>
            </select>
            <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-200 group-focus-within:w-full"></div>
          </div>
        </div> */}

        <div className="pt-2">
          {error && <p className="text-red-500 mb-3">{error}</p>}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center py-3 px-4 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 disabled:opacity-70"
          >
            {isLoading ? (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : null}
            {isLoading ? "Creating account..." : "Create account"}
          </button>
        </div>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Already have an account?
              </span>
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Sign in
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SignupForm;